import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Link,
  Progress,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { useState, useRef, ChangeEvent } from 'react';
import { MdUploadFile } from 'react-icons/md';
import FormModal from '../../../components/FormModal';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { translate } from '../../../i18n';
import { ImportJobStatus } from '../../../models/user/dto/import-job.dto';
import {
  READ_FAILED_I18N_KEY,
  classifyRead,
  scopePromptI18nKey,
} from '../../_shared/readState';
import { useGetCohorts } from '../../user-cohort/hooks/useGetCohorts';
import { useCancelImportJob } from '../hooks/useCancelImportJob';
import { useConfirmImportJob } from '../hooks/useConfirmImportJob';
import { useCreateImportJob } from '../hooks/useCreateImportJob';
import {
  isTerminalImportStatus,
  useGetImportJob,
} from '../hooks/useGetImportJob';
import { useRollbackImportJob } from '../hooks/useRollbackImportJob';
import { MAX_FILE_SIZE } from '../utils/batch-upload.utils';

type BatchUploadUsersModalProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
};

export default function BatchUploadUsersModal({
  isOpen,
  onClose,
  organizationId,
}: BatchUploadUsersModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [parseError, setParseError] = useState<string>('');
  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cohort selection state
  const [cohortOption, setCohortOption] = useState<'none' | 'existing' | 'new'>(
    'none',
  );
  const [selectedCohortId, setSelectedCohortId] = useState<string>('');
  const [newCohortName, setNewCohortName] = useState<string>('');

  // R94-1 — `isError` is taken as well as `data`. This is the site where a
  // silent failure is most expensive in the OTHER direction: an operator who
  // picks "select existing cohort", is shown an empty list, and concludes the
  // organization has none will reach for "create new" — and the read that
  // failed is the only thing that could have told them the cohort already
  // exists. A refused read now says so rather than inviting the duplicate.
  //
  // R116-3 — converted onto `classifyRead` so the four states are named in one
  // place (D59) rather than derived from a raw flag at the render site. Two of
  // the three new arms are honest bookkeeping rather than fixes, and saying so
  // is the point:
  //
  //   never-issued  UNREACHABLE today, measured rather than assumed. The gate
  //                 is `enabled: Boolean(organizationId)`, and this component's
  //                 only caller renders it inside `{selectedOrganization && …}`
  //                 (`UserList.tsx`), so the single gate term is completely
  //                 re-derived by the guard one file up. The arm is written
  //                 anyway for D62's reason — a guard living in a different
  //                 expression from the gate stops covering it the day a term is
  //                 added — and the caller guard is PINNED by
  //                 `user-management-read-state.test.tsx`, so the premise cannot
  //                 rot unnoticed the way D47's allowlist entry did.
  //   loading       real but transient: the select used to render with zero
  //                 options while the read was in flight, which is the same
  //                 empty chooser one moment early.
  //
  // The refused arm below is unchanged in behaviour — `isUnreadable` requires
  // `!data`, so the only difference is that a failed BACKGROUND refetch over a
  // cohort list already on screen now keeps the list instead of replacing the
  // select with an error (R96-3).
  const {
    data: cohorts,
    isError: isCohortsError,
    isInitialLoading: isCohortsInitialLoading,
  } = useGetCohorts(organizationId);
  const cohortsReadState = classifyRead(
    isCohortsError,
    isCohortsInitialLoading,
    cohorts,
  );
  const { mutate: createImportJob, isLoading: isUploading } =
    useCreateImportJob(organizationId);
  // R127-2 — the ONE read on this modal that is about a write already in
  // flight, and the only one that had no evidence of its own.
  //
  // `{ data: job }` alone conflates "no import has been started" with "the
  // import IS running and we could not read it", because both leave `job`
  // undefined. The consequences are not symmetric with the rest of this
  // campaign's finds, because a server-side job exists in the second case:
  //
  //   * `{job && …}` hid the whole progress panel, so the READY phase-2 gate —
  //     "Confirm import (N)" and "Discard" — became unreachable for a job that
  //     was sitting there waiting for exactly that decision, with the CSV still
  //     stored server-side.
  //   * `isProcessing` is `!!jobId && !isTerminalImportStatus(job?.status)`,
  //     and `isTerminalImportStatus(undefined)` is false, so a refused read
  //     pinned the modal's submit button in its loading state FOREVER while
  //     claiming, by that spinner, that the import was progressing.
  //
  // `never-issued` is the modal's ordinary resting state here (the gate is
  // `enabled: !!jobId`) and must stay silent — this is the entity-gated shape
  // the sibling gate's docblock describes, not a scope prompt. Only
  // `unreadable` gets an affordance, and it is deliberately a "we could not
  // read it" rather than a failure claim: the job may well be running. The
  // poller keeps firing every 2s through an error (`refetchInterval` returns
  // 2000 while `data` is undefined), so the notice clears itself the moment the
  // API answers again.
  const {
    data: job,
    isError: isJobError,
    isInitialLoading: isJobInitialLoading,
  } = useGetImportJob(organizationId, jobId);
  const jobReadState = classifyRead(isJobError, isJobInitialLoading, job);
  const isJobUnreadable = jobReadState === 'unreadable';
  const { mutate: rollback, isLoading: isRollingBack } =
    useRollbackImportJob(organizationId);
  const { mutate: confirmImport, isLoading: isConfirming } =
    useConfirmImportJob(organizationId);
  const { mutate: cancelImport, isLoading: isCanceling } =
    useCancelImportJob(organizationId);
  const [rolledBackCount, setRolledBackCount] = useState<number | null>(null);
  const [rolledBackKeptCount, setRolledBackKeptCount] = useState<number>(0);
  const [rollbackError, setRollbackError] = useState<string>('');

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const bgColor = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const invalidRowBg = useColorModeValue('red.50', 'red.900');

  // The server parses + validates at upload, so the job itself carries the
  // canonical preview: READY means "here's exactly what will happen, proceed?".
  const isAwaitingConfirm = job?.status === ImportJobStatus.READY;
  // An unreadable job is not a processing job — it is a job we cannot see. The
  // spinner is a claim about the server's state and we no longer have one.
  const isProcessing =
    !!jobId
    && !isJobUnreadable
    && !isTerminalImportStatus(job?.status)
    && !isAwaitingConfirm;
  const isDone = !!job && isTerminalImportStatus(job.status);
  const willImport = job
    ? Math.max(0, job.totalRows - job.errorCount - job.skippedCount)
    : 0;

  const resetForm = () => {
    setFile(null);
    setFileName('');
    setParseError('');
    setJobId(undefined);
    setRolledBackCount(null);
    setRolledBackKeptCount(0);
    setRollbackError('');
    setCohortOption('none');
    setSelectedCohortId('');
    setNewCohortName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (!selected) return;

    setParseError('');
    setFileName('');
    setFile(null);
    setJobId(undefined);
    // Clear any prior run's terminal state so a stale "rolled back" / progress
    // panel doesn't linger when the operator picks a new file without closing.
    setRolledBackCount(null);
    setRolledBackKeptCount(0);
    setRollbackError('');

    if (!selected.name.endsWith('.csv')) {
      setParseError(translate('user.please_upload_csv'));
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setParseError(translate('user.file_too_large'));
      return;
    }

    setFileName(selected.name);
    setFile(selected);
    // No client-side parse: the upload itself validates server-side (~230ms even
    // at 18K rows) and comes back READY with the canonical counts. One parser,
    // not two — the preview can no longer disagree with what actually imports.
  };

  const handleImport = () => {
    if (!file) return;
    createImportJob(
      {
        file,
        existingCohortId:
          cohortOption === 'existing' ? selectedCohortId : undefined,
        newCohortName: cohortOption === 'new' ? newCohortName : undefined,
      },
      {
        onSuccess: (createdJob) => setJobId(createdJob.id),
        onError: (error: any) =>
          setParseError(error.message || translate('user.import_start_failed')),
      },
    );
  };

  // Phase 2: the operator approved the server-validated counts → queue the run.
  const handleConfirmImport = () => {
    if (!jobId) return;
    confirmImport(jobId, {
      onError: (error: any) =>
        setParseError(error?.message || translate('user.import_start_failed')),
    });
  };

  // Discard a previewed import; the server also reclaims the stored CSV.
  const handleCancelImport = () => {
    if (!jobId) return;
    cancelImport(jobId, {
      onSuccess: () => resetForm(),
      onError: (error: any) =>
        setParseError(error?.message || translate('user.import_start_failed')),
    });
  };

  const handleRollback = () => {
    if (!jobId) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm(translate('user.rollback_confirm'));
    if (!ok) return;
    setRollbackError('');
    rollback(jobId, {
      onSuccess: (r) => {
        setRolledBackCount(r.deletedCount);
        setRolledBackKeptCount(r.keptCount);
      },
      onError: (error: any) =>
        setRollbackError(error?.message || translate('user.rollback_failed')),
    });
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/sample-users-import.csv';
    link.download = 'sample-users-import.csv';
    link.click();
  };

  const handleModalClose = () => {
    resetForm();
    onClose();
  };

  const progressPct =
    job && job.totalRows > 0
      ? Math.round((job.processedRows / job.totalRows) * 100)
      : 0;

  const statusColor =
    job?.status === ImportJobStatus.COMPLETED
      ? 'green'
      : job?.status === ImportJobStatus.FAILED
        ? 'red'
        : job?.status === ImportJobStatus.PARTIAL
          ? 'orange'
          : 'brand';

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={translate('user.import_users')}
      onSubmit={handleImport}
      submitLabel={translate('user.import_users')}
      cancelLabel={isDone ? translate('common.close') : translate('common.cancel')}
      isSubmitting={isUploading || isProcessing}
      isSubmitDisabled={!file || isUploading || !!jobId}
      size="6xl"
    >
      <Flex direction="column" gap={4}>
        {/* Instructions */}
        <Box>
          <Text fontWeight="600" mb={2} color={textColor}>
            {translate('user.import_instructions')}:
          </Text>
          <Text fontSize="sm" color="gray.500" mb={2}>
            1. {translate('user.csv_must_have_columns')}: Email, FirstName,
            LastName
          </Text>
          <Text fontSize="sm" color="gray.500" mb={2}>
            2. {translate('user.optional_columns')}: WalletBalance, PhoneNumber,
            PostalCode, Gender (Sexe), Membership (Abonnement)
          </Text>
          <Text fontSize="sm" color="gray.500" mb={2}>
            3. {translate('user.extra_columns_kept')}
          </Text>
          <Link
            color="brand.500"
            fontSize="sm"
            onClick={handleDownloadTemplate}
            cursor="pointer"
            textDecoration="underline"
          >
            {translate('user.download_sample_template')}
          </Link>
        </Box>

        {/* File upload */}
        <Box>
          <Input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            display="none"
            id="csv-upload"
            isDisabled={!!jobId}
          />
          <Button
            as="label"
            htmlFor="csv-upload"
            leftIcon={<Icon as={MdUploadFile} />}
            colorScheme="brand"
            variant="outline"
            cursor="pointer"
            w="full"
            isDisabled={!!jobId}
          >
            {fileName || translate('user.select_csv_file')}
          </Button>
        </Box>

        {/* Parse error */}
        {parseError && (
          <Alert status="error">
            <AlertIcon />
            {parseError}
          </Alert>
        )}

        {/* Cohort assignment */}
        <Box
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          p={4}
          bg={bgColor}
        >
          <FormControl>
            <FormLabel fontWeight="600" color={textColor}>
              {translate('user.assign_to_cohort')} (
              {translate('user.optional')})
            </FormLabel>
            <RadioGroup
              value={cohortOption}
              onChange={(value: any) => setCohortOption(value)}
              isDisabled={!!jobId}
            >
              <Stack spacing={3}>
                <Radio value="none">{translate('user.no_cohort')}</Radio>
                <Radio value="existing">
                  {translate('user.select_existing_cohort')}
                </Radio>
                {cohortOption === 'existing' && (
                  <Box ml={6} maxW="100%" minW={0}>
                    {cohortsReadState === 'unreadable' ? (
                      <InlineErrorState
                        message={translate('cohort.error_loading')}
                      />
                    ) : cohortsReadState === 'never-issued' ? (
                      // Unreachable today — see the classification above. The
                      // arm exists so that adding a second gate term to
                      // `useGetCohorts` cannot silently reintroduce an empty
                      // chooser here.
                      <InlineErrorState
                        message={translate(
                          scopePromptI18nKey('organization') as any,
                        )}
                      />
                    ) : (
                      <Select
                        value={selectedCohortId}
                        onChange={(e) => setSelectedCohortId(e.target.value)}
                        isDisabled={cohortsReadState === 'loading'}
                        placeholder={translate(
                          cohortsReadState === 'loading'
                            ? 'cohort.loading'
                            : 'user.select_cohort',
                        )}
                        w="100%"
                        maxW="100%"
                      >
                        {cohorts?.map((cohort) => (
                          <option key={cohort.id} value={cohort.id}>
                            {cohort.name} ({cohort.memberCount || 0}{' '}
                            {translate('user.members')})
                          </option>
                        ))}
                      </Select>
                    )}
                  </Box>
                )}
                <Radio value="new">
                  {translate('user.create_new_cohort')}
                </Radio>
                {cohortOption === 'new' && (
                  <Box ml={6}>
                    <Input
                      placeholder={translate('user.cohort_name')}
                      value={newCohortName}
                      onChange={(e) => setNewCohortName(e.target.value)}
                    />
                  </Box>
                )}
              </Stack>
            </RadioGroup>
          </FormControl>
        </Box>

        {/* The import exists server-side and we could not read it. Said out
            loud rather than rendered as "no import running", because the CSV is
            stored, the job may be mid-run, and starting a second one is the
            thing an operator does when a screen tells them nothing happened. */}
        {isJobUnreadable && (
          <InlineErrorState message={translate(READ_FAILED_I18N_KEY)} />
        )}

        {/* Live import progress (async job) */}
        {job && (
          <Box
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            p={4}
            bg={bgColor}
          >
            <Flex justify="space-between" align="center" mb={2}>
              <Text fontWeight="600" color={textColor}>
                {isAwaitingConfirm
                  ? translate('user.import_ready')
                  : isDone
                    ? translate('user.import_finished')
                    : translate('user.importing')}
              </Text>
              <Badge colorScheme={statusColor}>{job.status}</Badge>
            </Flex>
            {/* No progress bar while awaiting confirmation — nothing is running. */}
            {!isAwaitingConfirm && (
              <Progress
                value={progressPct}
                colorScheme={statusColor}
                hasStripe={isProcessing}
                isAnimated={isProcessing}
                borderRadius="md"
                mb={3}
              />
            )}
            <Flex gap={3} wrap="wrap">
              {isAwaitingConfirm ? (
                <>
                  <Badge colorScheme="green">
                    {translate('user.will_import')}: {willImport}
                  </Badge>
                  {job.skippedCount > 0 && (
                    <Badge colorScheme="yellow">
                      {translate('user.duplicates')}: {job.skippedCount}
                    </Badge>
                  )}
                  {job.errorCount > 0 && (
                    <Badge colorScheme="red">
                      {translate('user.invalid')}: {job.errorCount}
                    </Badge>
                  )}
                  <Badge colorScheme="gray">
                    {translate('user.total')}: {job.totalRows}
                  </Badge>
                </>
              ) : (
                <>
                  <Badge colorScheme="gray">
                    {job.processedRows}/{job.totalRows}
                  </Badge>
                  <Badge colorScheme="green">
                    {translate('user.valid')}: {job.successCount}
                  </Badge>
                  {job.skippedCount > 0 && (
                    <Badge colorScheme="yellow">
                      {translate('user.skipped')}: {job.skippedCount}
                    </Badge>
                  )}
                  {job.errorCount > 0 && (
                    <Badge colorScheme="red">
                      {translate('user.errors')}: {job.errorCount}
                    </Badge>
                  )}
                </>
              )}
            </Flex>
            {/* Phase 2 gate: nothing was created yet — the operator decides. */}
            {isAwaitingConfirm && (
              <Flex gap={3} mt={3}>
                <Button
                  size="sm"
                  colorScheme="brand"
                  isLoading={isConfirming}
                  isDisabled={willImport === 0}
                  onClick={handleConfirmImport}
                >
                  {translate('user.confirm_import')} ({willImport})
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  isLoading={isCanceling}
                  onClick={handleCancelImport}
                >
                  {translate('user.discard')}
                </Button>
              </Flex>
            )}
            {job.fatalError && (
              <Alert status="error" mt={3}>
                <AlertIcon />
                {job.fatalError}
              </Alert>
            )}
            {/* Undo: delete the still-unclaimed rows this run created. */}
            {isDone && rolledBackCount === null && job.successCount > 0 && (
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                mt={3}
                isLoading={isRollingBack}
                onClick={handleRollback}
              >
                {translate('user.undo_import')}
              </Button>
            )}
            {rollbackError && (
              <Alert status="error" mt={3}>
                <AlertIcon />
                {rollbackError}
              </Alert>
            )}
            {rolledBackCount !== null && (
              <Alert status="info" mt={3}>
                <AlertIcon />
                {translate('user.rolled_back', { count: rolledBackCount })}
                {rolledBackKeptCount > 0 &&
                  ` ${translate('user.rolled_back_kept', {
                    count: rolledBackKeptCount,
                  })}`}
              </Alert>
            )}
            {(isDone || isAwaitingConfirm) && job.errors.length > 0 && (
              <Box
                mt={3}
                maxH="200px"
                overflowY="auto"
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="md"
              >
                <Table size="sm" variant="simple">
                  <Thead position="sticky" top={0} bg={bgColor} zIndex={1}>
                    <Tr>
                      <Th borderColor={borderColor}>
                        {translate('user.row')}
                      </Th>
                      <Th borderColor={borderColor}>
                        {translate('user.email')}
                      </Th>
                      <Th borderColor={borderColor}>
                        {translate('user.reason')}
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {job.errors.map((e, i) => (
                      <Tr key={i}>
                        <Td borderColor={borderColor}>{e.row}</Td>
                        <Td borderColor={borderColor} fontSize="sm">
                          {e.email}
                        </Td>
                        <Td borderColor={borderColor} fontSize="sm">
                          {e.reason}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        )}

      </Flex>
    </FormModal>
  );
}
